import { createContext, useContext, useEffect, useState } from 'react';
import { apiRequest } from '../lib/apiClient';

const AuthContext = createContext(null);

const emailAuthUrl = process.env.REACT_APP_AUTH_EMAIL_URL || '';
const googleAuthUrl = process.env.REACT_APP_AUTH_GOOGLE_URL || '';

function buildAuthRedirect(url, email) {
  if (!url) return null;

  const target = new URL(url, window.location.origin);
  target.searchParams.set('redirect_uri', `${window.location.origin}/auth/callback`);
  if (email) {
    target.searchParams.set('email', email);
  }
  return target.toString();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const hasRemoteAuth = Boolean(emailAuthUrl || googleAuthUrl);

  useEffect(() => {
    let active = true;

    const loadCurrentUser = async () => {
      try {
        const data = await apiRequest('/api/auth/me', { method: 'GET' });
        if (active) {
          setUser(data);
          setAuthError(null);
        }
      } catch {
        if (active) {
          setUser(null);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadCurrentUser();

    return () => {
      active = false;
    };
  }, []);

  const signInWithPassword = async (email, password) => {
    try {
      setAuthError(null);
      const data = await apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setUser(data.user);
      return { data };
    } catch (error) {
      setAuthError(error.message);
      return { error };
    }
  };

  const registerWithPassword = async (name, email, password) => {
    try {
      setAuthError(null);
      const data = await apiRequest('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ name, email, password }),
      });
      setUser(data.user);
      return { data };
    } catch (error) {
      setAuthError(error.message);
      return { error };
    }
  };

  const signInWithEmail = async (email) => {
    setAuthError(null);
    const target = buildAuthRedirect(emailAuthUrl, email);
    if (!target) {
      const error = { message: 'Magic link sign-in is not configured.' };
      setAuthError(error.message);
      return { error };
    }

    window.location.href = target;
    return { data: { redirecting: true } };
  };

  const signInWithGoogle = async () => {
    setAuthError(null);
    const target = buildAuthRedirect(googleAuthUrl);
    if (!target) {
      const error = { message: 'Google sign-in is not configured.' };
      setAuthError(error.message);
      return { error };
    }

    window.location.href = target;
    return { data: { redirecting: true } };
  };

  const signOut = async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    session: user,
    loading,
    authError,
    isAuthenticated: Boolean(user),
    isConfigured: true,
    hasRemoteAuth,
    signInWithPassword,
    registerWithPassword,
    signInWithEmail,
    signInWithGoogle,
    signOut,
    clearError: () => setAuthError(null),
    refreshUser: async () => {
      const data = await apiRequest('/api/auth/me', { method: 'GET' });
      setUser(data);
      return data;
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
